/**
 * SettingsModal Component
 * 
 * Modal component for configuring office employee payslip settings.
 * Provides comprehensive configuration options for:
 * - General settings (font size, font family)
 * - Appearance settings (border color, background color)
 * - Field selection (enable/disable payment, deduction, attendance, notes fields)
 * - Display settings (show/hide company name, employee code, memo)
 * 
 * Features:
 * - Tabbed interface for organizing settings
 * - Temporary settings state (changes aren't saved until "保存" is clicked)
 * - Reset to defaults functionality
 * - Clear all settings functionality
 * - Separate settings for salary and bonus payslip types
 * - Field selection with "select all" and "deselect all" options
 * - Font preview in general settings
 * - Last updated timestamp tracking
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { clearSettings } from './SettingsUtils';

/**
 * Props interface for SettingsModal component.
 */
interface SettingsModalProps {
  isOpen: boolean;                             // Whether modal is open
  onClose: () => void;                         // Handler to close modal
  dataType: 'salary' | 'bonus';               // Payslip type (affects title and defaults)
  settings: {
    borderColor: string;
    backgroundColor: string;
    fontSize: string;
    fontFamily: string;
    showCompanyName: boolean;
    showEmployeeCode: boolean;
    showMemo: boolean;
    payment: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    deduction: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    attendance: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    notes: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
  };
  onSettingsChange: (settings: {
    borderColor: string;
    backgroundColor: string;
    fontSize: string;
    fontFamily: string;
    showCompanyName: boolean;
    showEmployeeCode: boolean;
    showMemo: boolean;
    payment: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    deduction: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    attendance: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    notes: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
  }) => void;
  paymentItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  deductionItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  attendanceItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  notesItems: Array<{ id: string; name: string; keyId: string; valueId: string }>; // Notes field items
}

/**
 * SettingsModal Component
 * 
 * Renders a modal dialog for configuring payslip settings.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const SettingsModal: React.FC<SettingsModalProps> = ({ 
  isOpen, 
  onClose, 
  dataType, 
  settings, 
  onSettingsChange, 
  paymentItems, 
  deductionItems, 
  attendanceItems, 
  notesItems 
}) => {
  /**
   * State management:
   * - activeTab: Current main tab (general, appearance, fields, display)
   * - activeFieldTab: Current field tab (for fields tab - payment, deduction, attendance, notes)
   * - originalSettings: Settings state when modal was opened (for cancel functionality)
   * - tempSettings: Temporary settings state (changes aren't saved until user clicks "保存")
   */
  const [activeTab, setActiveTab] = useState<'general' | 'appearance' | 'fields' | 'display'>('general');
  const [activeFieldTab, setActiveFieldTab] = useState<'payment' | 'deduction' | 'attendance' | 'notes'>('payment');

  /**
   * Store original settings when modal opens.
   * Used for cancel functionality to restore original state.
   */
  const [originalSettings, setOriginalSettings] = useState(settings);
  
  /**
   * Temporary settings state.
   * All changes are made to tempSettings first, then saved when user clicks "保存".
   */
  const [tempSettings, setTempSettings] = useState(settings);

  /**
   * Update temp settings when modal opens, settings prop changes, or dataType changes.
   * 
   * Resets both originalSettings and tempSettings to current props.settings when:
   * - Modal opens (isOpen becomes true)
   * - Settings prop changes
   * - Data type changes (switching between salary and bonus)
   */
  useEffect(() => {
    if (isOpen) {
      setOriginalSettings(settings);
      setTempSettings(settings);
    }
  }, [isOpen, settings, dataType]);

  /**
   * Ensure all settings have default values to prevent undefined errors.
   * 
   * Provides fallback values for nested field settings (payment, deduction, attendance, notes)
   * to prevent errors when accessing properties on older smartphones.
   */
  const safeSettings = {
    ...tempSettings,
    payment: tempSettings.payment || { enabled: [], lastUpdated: '', updatedBy: '' },
    deduction: tempSettings.deduction || { enabled: [], lastUpdated: '', updatedBy: '' },
    attendance: tempSettings.attendance || { enabled: [], lastUpdated: '', updatedBy: '' },
    notes: tempSettings.notes || { enabled: [], lastUpdated: '', updatedBy: '' }
  };

  /**
   * Handles color change (border or background).
   * 
   * Updates tempSettings with new color value while preserving all other settings.
   * 
   * @param type - Color type: 'border' or 'background'
   * @param value - New color value (hex string, e.g., '#4caf50')
   */
  const handleColorChange = (type: 'border' | 'background', value: string) => {
    const newSettings = {
      ...safeSettings,
      [type === 'border' ? 'borderColor' : 'backgroundColor']: value,
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance, // Preserve attendance settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles font change (size or family).
   * 
   * Updates tempSettings with new font value while preserving all other settings.
   * 
   * @param type - Font type: 'size' or 'family'
   * @param value - New font value (size: e.g., '11px', family: e.g., 'Yu Gothic')
   */
  const handleFontChange = (type: 'size' | 'family', value: string) => {
    const newSettings = {
      ...safeSettings,
      [type === 'size' ? 'fontSize' : 'fontFamily']: value,
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance, // Preserve attendance settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles display option change (company name, employee code, or memo).
   * 
   * Updates tempSettings with new display option value while preserving all other settings.
   * 
   * @param type - Display type: 'company', 'employee', or 'memo'
   * @param value - New display value (boolean: true to show, false to hide)
   */
  const handleDisplayChange = (type: 'company' | 'employee' | 'memo', value: boolean) => {
    const newSettings = {
      ...safeSettings,
      [type === 'company' ? 'showCompanyName' : type === 'employee' ? 'showEmployeeCode' : 'showMemo']: value,
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance, // Preserve attendance settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles payment item enable/disable change.
   * 
   * Adds or removes item ID from payment.enabled array and updates lastUpdated timestamp.
   * 
   * @param itemId - Payment item ID to toggle
   * @param enabled - Whether item should be enabled (true) or disabled (false)
   */
  const handlePaymentItemChange = (itemId: string, enabled: boolean) => {
    const newEnabled = enabled
      ? [...safeSettings.payment.enabled, itemId]
      : safeSettings.payment.enabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      payment: {
        ...safeSettings.payment,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance // Preserve attendance settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles deduction item enable/disable change.
   * 
   * Adds or removes item ID from deduction.enabled array and updates lastUpdated timestamp.
   * 
   * @param itemId - Deduction item ID to toggle
   * @param enabled - Whether item should be enabled (true) or disabled (false)
   */
  const handleDeductionItemChange = (itemId: string, enabled: boolean) => {
    const newEnabled = enabled
      ? [...safeSettings.deduction.enabled, itemId]
      : safeSettings.deduction.enabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      deduction: {
        ...safeSettings.deduction,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      attendance: safeSettings.attendance // Preserve attendance settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles attendance item enable/disable change.
   * 
   * Adds or removes item ID from attendance.enabled array and updates lastUpdated timestamp.
   * 
   * @param itemId - Attendance item ID to toggle
   * @param enabled - Whether item should be enabled (true) or disabled (false)
   */
  const handleAttendanceItemChange = (itemId: string, enabled: boolean) => {
    const newEnabled = enabled
      ? [...safeSettings.attendance.enabled, itemId]
      : safeSettings.attendance.enabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      attendance: {
        ...safeSettings.attendance,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles notes item enable/disable change.
   * 
   * Adds or removes item ID from notes.enabled array and updates lastUpdated timestamp.
   * 
   * @param itemId - Notes item ID to toggle
   * @param enabled - Whether item should be enabled (true) or disabled (false)
   */
  const handleNotesItemChange = (itemId: string, enabled: boolean) => {
    const newEnabled = enabled
      ? [...safeSettings.notes.enabled, itemId]
      : safeSettings.notes.enabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      notes: {
        ...safeSettings.notes,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance // Preserve attendance settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Resets payment items to default (first 9 items enabled).
   * 
   * Sets payment.enabled to first 9 payment items and updates lastUpdated timestamp.
   */
  const resetPaymentToDefault = () => {
    const newSettings = {
      ...safeSettings,
      payment: {
        ...safeSettings.payment,
        enabled: paymentItems.slice(0, 9).map(item => item.id),
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance, // Preserve attendance settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Resets deduction items to default (first 9 items enabled).
   * 
   * Sets deduction.enabled to first 9 deduction items and updates lastUpdated timestamp.
   */
  const resetDeductionToDefault = () => {
    const newSettings = {
      ...safeSettings,
      deduction: {
        ...safeSettings.deduction,
        enabled: deductionItems.slice(0, 9).map(item => item.id),
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      attendance: safeSettings.attendance, // Preserve attendance settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Resets attendance items to default (first 9 items enabled).
   * 
   * Sets attendance.enabled to first 9 attendance items and updates lastUpdated timestamp.
   */
  const resetAttendanceToDefault = () => {
    const newSettings = {
      ...safeSettings,
      attendance: {
        ...safeSettings.attendance,
        enabled: attendanceItems.slice(0, 9).map(item => item.id),
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      notes: safeSettings.notes // Preserve notes settings
    };
    setTempSettings(newSettings);
  };

  /**
   * Resets notes items to default (first 5 items enabled).
   * 
   * Sets notes.enabled to first 5 notes items and updates lastUpdated timestamp.
   */
  const resetNotesToDefault = () => {
    const newSettings = {
      ...safeSettings,
      notes: {
        ...safeSettings.notes,
        enabled: notesItems.slice(0, 5).map(item => item.id),
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      },
      payment: safeSettings.payment, // Preserve payment settings
      deduction: safeSettings.deduction, // Preserve deduction settings
      attendance: safeSettings.attendance // Preserve attendance settings
    };
    setTempSettings(newSettings);
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      {/* Modal container - stopPropagation prevents closing when clicking inside modal */}
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="settings-modal-header">
          <h3>{dataType === 'salary' ? '給与' : '賞与'}明細書設定</h3>
          <button className="settings-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="settings-modal-content">
          {/* Sidebar Navigation */}
          <div className="settings-sidebar">
            <button 
              className={`settings-nav-item ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
             ✏️ 一般設定
            </button>
            <button 
              className={`settings-nav-item ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              🎨 外観設定
            </button>
            <button 
              className={`settings-nav-item ${activeTab === 'fields' ? 'active' : ''}`}
              onClick={() => setActiveTab('fields')}
            >
              📝 フィールド選択
            </button>
            <button 
              className={`settings-nav-item ${activeTab === 'display' ? 'active' : ''}`}
              onClick={() => setActiveTab('display')}
            >
               📊  表示設定
            </button>
          </div>

          {/* Main Content Area */}
          <div className="settings-main-content">
            {/* General Settings Tab */}
            {activeTab === 'general' && (
              <div className="settings-section">
                <h4>基本設定</h4>
                {/* Font Size Selector */}
                <div className="setting-item">
                  <label>フォントサイズ:</label>
                  <select 
                    value={safeSettings.fontSize}
                    onChange={(e) => handleFontChange('size', e.target.value)}
                  >
                    <option value="6px">6px</option>
                    <option value="8px">8px</option>
                    <option value="9px">9px</option>
                    <option value="10px">10px</option>
                    <option value="11px">11px</option>
                    <option value="12px">12px</option>
                    <option value="13px">13px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                  </select>
                </div>
                {/* Font Family Selector */}
                <div className="setting-item">
                  <label>フォント:</label>
                  <select 
                    value={safeSettings.fontFamily}
                    onChange={(e) => handleFontChange('family', e.target.value)}
                  >
                    <option value="Yu Gothic">Yu Gothic</option>
                    <option value="MS Gothic">MS Gothic</option>
                    <option value="Hiragino Kaku Gothic ProN">Hiragino Kaku Gothic ProN</option>
                    <option value="Noto Sans JP">Noto Sans JP</option>
                    <option value="Meiryo">Meiryo</option>
                  </select>
                </div>
                {/* Font Preview - Shows how selected font looks */}
                <div className="font-preview">
                  <label>プレビュー:</label>
                  <div 
                    className="font-preview-text"
                    style={{
                      fontFamily: safeSettings.fontFamily,
                      fontSize: safeSettings.fontSize
                    }}
                  >
                    給与明細書 - 基本給 ¥300,000
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Settings Tab */}
            {activeTab === 'appearance' && (
              <div className="settings-section">
                <h4>色設定</h4>
                {/* Default Green Color Checkbox */}
                <div className="setting-item">
                  <label>デフォルト緑色を使用:</label>
                  <input 
                    type="checkbox" 
                    checked={safeSettings.borderColor === '#4caf50' && safeSettings.backgroundColor === '#eaffea'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        /**
                         * Reset to default green colors.
                         * Default: borderColor = '#4caf50', backgroundColor = '#eaffea'
                         */
                        const newSettings = {
                          ...safeSettings,
                          borderColor: '#4caf50',
                          backgroundColor: '#eaffea',
                          payment: safeSettings.payment, // Preserve payment settings
                          deduction: safeSettings.deduction, // Preserve deduction settings
                          attendance: safeSettings.attendance // Preserve attendance settings
                        };
                        onSettingsChange(newSettings);
                      }
                    }}
                  />
                </div>
                {/* Border Color Picker */}
                <div className="setting-item">
                  <label>テーブルボーダー色:</label>
                  <input 
                    type="color" 
                    value={safeSettings.borderColor}
                    onChange={(e) => handleColorChange('border', e.target.value)}
                  />
                </div>
                {/* Background Color Picker */}
                <div className="setting-item">
                  <label>背景色:</label>
                  <input 
                    type="color" 
                    value={safeSettings.backgroundColor}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Fields Selection Tab */}
            {activeTab === 'fields' && (
              <div className="settings-section">
                <h4>フィールド選択</h4>
                {/* Field Type Tabs (Payment, Deduction, Attendance, Notes) */}
                <div className="field-selection-tabs">
                  <button 
                    className={`field-tab-btn ${activeFieldTab === 'payment' ? 'active' : ''}`}
                    onClick={() => setActiveFieldTab('payment')}
                  >
                    支給項目
                  </button>
                  <button 
                    className={`field-tab-btn ${activeFieldTab === 'deduction' ? 'active' : ''}`}
                    onClick={() => setActiveFieldTab('deduction')}
                  >
                    控除項目
                  </button>
                  <button 
                    className={`field-tab-btn ${activeFieldTab === 'attendance' ? 'active' : ''}`}
                    onClick={() => setActiveFieldTab('attendance')}
                  >
                    出勤項目
                  </button>
                  <button 
                    className={`field-tab-btn ${activeFieldTab === 'notes' ? 'active' : ''}`}
                    onClick={() => setActiveFieldTab('notes')}
                  >
                    差引項目
                  </button>
                </div>

                {activeFieldTab === 'payment' && (
                  <div className="payment-section-header">
                    <h5>表示する支給項目を選択</h5>
                    <div className="payment-stats">
                      <div className="payment-stat">
                        選択済み: <span className="payment-stat-value">{safeSettings.payment.enabled.length}</span>
                      </div>
                      <div className="payment-stat">
                        合計: <span className="payment-stat-value">{paymentItems.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeFieldTab === 'deduction' && (
                  <div className="payment-section-header">
                    <h5>表示する控除項目を選択</h5>
                    <div className="payment-stats">
                      <div className="payment-stat">
                        選択済み: <span className="payment-stat-value">{safeSettings.deduction.enabled.length}</span>
                      </div>
                      <div className="payment-stat">
                        合計: <span className="payment-stat-value">{deductionItems.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeFieldTab === 'attendance' && (
                  <div className="payment-section-header">
                    <h5>表示する出勤項目を選択</h5>
                    <div className="payment-stats">
                      <div className="payment-stat">
                        選択済み: <span className="payment-stat-value">{safeSettings.attendance.enabled.length}</span>
                      </div>
                      <div className="payment-stat">
                        合計: <span className="payment-stat-value">{attendanceItems.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeFieldTab === 'notes' && (
                  <div className="payment-section-header">
                    <h5>表示する差引項目を選択</h5>
                    <div className="payment-stats">
                      <div className="payment-stat">
                        選択済み: <span className="payment-stat-value">{safeSettings.notes.enabled.length}</span>
                      </div>
                      <div className="payment-stat">
                        合計: <span className="payment-stat-value">{notesItems.length}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="payment-controls">
                  <label className="payment-control-radio">
                    <input
                      type="radio"
                      name="field-select-all"
                      checked={
                        activeFieldTab === 'payment' ? safeSettings.payment.enabled.length === paymentItems.length :
                        activeFieldTab === 'deduction' ? safeSettings.deduction.enabled.length === deductionItems.length :
                        activeFieldTab === 'attendance' ? safeSettings.attendance.enabled.length === attendanceItems.length :
                        activeFieldTab === 'notes' ? safeSettings.notes.enabled.length === notesItems.length :
                        false
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (activeFieldTab === 'payment') {
                            const newSettings = {
                              ...safeSettings,
                              payment: {
                                ...safeSettings.payment,
                                enabled: paymentItems.map(item => item.id),
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'deduction') {
                            const newSettings = {
                              ...safeSettings,
                              deduction: {
                                ...safeSettings.deduction,
                                enabled: deductionItems.map(item => item.id),
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'attendance') {
                            const newSettings = {
                              ...safeSettings,
                              attendance: {
                                ...safeSettings.attendance,
                                enabled: attendanceItems.map(item => item.id),
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              notes: safeSettings.notes // Preserve notes settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'notes') {
                            const newSettings = {
                              ...safeSettings,
                              notes: {
                                ...safeSettings.notes,
                                enabled: notesItems.map(item => item.id),
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          }
                        }
                      }}
                    />
                    <span>すべて選択</span>
                  </label>
                  {/* Deselect All Radio Button */}
                  <label className="payment-control-radio">
                    <input
                      type="radio"
                      name="field-select-all"
                      checked={
                        activeFieldTab === 'payment' ? safeSettings.payment.enabled.length === 0 :
                        activeFieldTab === 'deduction' ? safeSettings.deduction.enabled.length === 0 :
                        activeFieldTab === 'attendance' ? safeSettings.attendance.enabled.length === 0 :
                        activeFieldTab === 'notes' ? safeSettings.notes.enabled.length === 0 :
                        false
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          /**
                           * Disable all items for the active field tab.
                           * Sets enabled array to empty array and updates timestamp.
                           */
                          if (activeFieldTab === 'payment') {
                            const newSettings = {
                              ...safeSettings,
                              payment: {
                                ...safeSettings.payment,
                                enabled: [],
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'deduction') {
                            const newSettings = {
                              ...safeSettings,
                              deduction: {
                                ...safeSettings.deduction,
                                enabled: [],
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'attendance') {
                            const newSettings = {
                              ...safeSettings,
                              attendance: {
                                ...safeSettings.attendance,
                                enabled: [],
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              notes: safeSettings.notes // Preserve notes settings
                            };
                            onSettingsChange(newSettings);
                          } else if (activeFieldTab === 'notes') {
                            const newSettings = {
                              ...safeSettings,
                              notes: {
                                ...safeSettings.notes,
                                enabled: [],
                                lastUpdated: new Date().toISOString(),
                                updatedBy: 'admin'
                              },
                              payment: safeSettings.payment, // Preserve payment settings
                              deduction: safeSettings.deduction, // Preserve deduction settings
                              attendance: safeSettings.attendance // Preserve attendance settings
                            };
                            onSettingsChange(newSettings);
                          }
                        }
                      }}
                    />
                    <span>すべて解除</span>
                  </label>
                </div>

                <div className="payment-items-grid">
                  {activeFieldTab === 'payment' && paymentItems.map((item) => (
                    <label key={item.id} className="payment-item-checkbox">
                      <input
                        type="checkbox"
                        checked={safeSettings.payment.enabled.includes(item.id)}
                        onChange={(e) => handlePaymentItemChange(item.id, e.target.checked)}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                  {activeFieldTab === 'deduction' && deductionItems.map((item) => (
                    <label key={item.id} className="payment-item-checkbox">
                      <input
                        type="checkbox"
                        checked={safeSettings.deduction.enabled.includes(item.id)}
                        onChange={(e) => handleDeductionItemChange(item.id, e.target.checked)}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                  {activeFieldTab === 'attendance' && attendanceItems.map((item) => (
                    <label key={item.id} className="payment-item-checkbox">
                      <input
                        type="checkbox"
                        checked={safeSettings.attendance.enabled.includes(item.id)}
                        onChange={(e) => handleAttendanceItemChange(item.id, e.target.checked)}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                  {activeFieldTab === 'notes' && notesItems.map((item) => (
                    <label key={item.id} className="payment-item-checkbox">
                      <input
                        type="checkbox"
                        checked={safeSettings.notes.enabled.includes(item.id)}
                        onChange={(e) => handleNotesItemChange(item.id, e.target.checked)}
                      />
                      <span>{item.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Display Settings Tab */}
            {activeTab === 'display' && (
              <div className="settings-section">
                <h4>表示設定</h4>
                {/* Show Company Name Toggle */}
                <div className="setting-item">
                  <label>会社名を表示:</label>
                  <input 
                    type="checkbox" 
                    checked={safeSettings.showCompanyName}
                    onChange={(e) => handleDisplayChange('company', e.target.checked)}
                  />
                </div>
                {/* Show Employee Code Toggle */}
                <div className="setting-item">
                  <label>社員コードを表示:</label>
                  <input 
                    type="checkbox" 
                    checked={safeSettings.showEmployeeCode}
                    onChange={(e) => handleDisplayChange('employee', e.target.checked)}
                  />
                </div>
                {/* Show Memo Toggle */}
                <div className="setting-item">
                  <label>メモ欄を表示:</label>
                  <input 
                    type="checkbox" 
                    checked={safeSettings.showMemo}
                    onChange={(e) => handleDisplayChange('memo', e.target.checked)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer with Action Buttons */}
        <div className="settings-modal-footer">
          {/* Reset Button - Resets all settings to defaults */}
          <button 
            className="settings-reset-btn" 
            onClick={() => {
              /**
               * Reset all settings to default values.
               * Defaults:
               * - Colors: green (#4caf50 border, #eaffea background)
               * - Font: 11px Yu Gothic
               * - Display: all enabled (company name, employee code, memo)
               * - Fields: first 9 items for payment/deduction/attendance, all items for notes
               */
              const resetSettings = {
                borderColor: '#4caf50',
                backgroundColor: '#eaffea',
                fontSize: '11px',
                fontFamily: 'Yu Gothic',
                showCompanyName: true,
                showEmployeeCode: true,
                showMemo: true,
                payment: {
                  enabled: paymentItems.slice(0, 9).map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                deduction: {
                  enabled: deductionItems.slice(0, 9).map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                attendance: {
                  enabled: attendanceItems.slice(0, 9).map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                notes: {
                  enabled: notesItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                }
              };
              setTempSettings(resetSettings);
            }}
          >
            リセット
          </button>
          
          {/* Clear Button - Clears all settings from storage and reloads page */}
          <button 
            className="settings-clear-btn" 
            onClick={() => {
              /**
               * Clear all settings from storage.
               * Uses clearSettings utility to remove settings from localStorage/API,
               * then reloads the page to restore defaults.
               */
              clearSettings(dataType);
              // Force reload by closing and reopening modal
              onClose();
              setTimeout(() => {
                // This will trigger a reload of settings when modal reopens
                window.location.reload();
              }, 100);
            }}
            style={{
              backgroundColor: '#ff6b6b',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            設定クリア
          </button>
          
          {/* Save Button - Saves tempSettings and closes modal */}
          <button 
            className="settings-save-btn" 
            onClick={() => {
              /**
               * Save current tempSettings.
               * Calls onSettingsChange callback with current tempSettings,
               * which will persist settings to API/localStorage.
               */
              onSettingsChange(safeSettings);
              onClose();
            }}
          >
            保存
          </button>
          
          {/* Cancel Button - Restores originalSettings and closes modal */}
          <button 
            className="settings-cancel-btn" 
            onClick={() => {
              /**
               * Cancel changes and restore original settings.
               * Restores tempSettings to originalSettings (state when modal opened),
               * then closes modal without saving.
               */
              setTempSettings(originalSettings);
              onClose();
            }}
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
};
